import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  X,
  Minus,
  Maximize2,
  Paperclip,
  Trash2,
  Image,
  Send,
  Laugh,
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import TextEditorMail from "./TextEditorMail";
import { toast } from "react-toastify";
import { fetchJson } from "@utils/fetchJson";
import { useEmailCreateStore } from "./store/useMailStore";
import { API_BASE_URL } from "@config";

/**
 * 🧩 Reusable Email Pill Input with drag-move between fields
 */
function EmailPillInput({
  fieldName,
  value = [],
  onChange,
  placeholder,
  autoFocus,
  dragEmail,
  setDragEmail,
  onRemoveFromSource,
  onAddToTarget,
}) {
  const [inputValue, setInputValue] = useState("");

  const commitInput = () => {
    const newEmail = inputValue.trim();
    if (newEmail && !value.find((v) => v.email === newEmail)) {
      onChange([...value, { email: newEmail }]);
    }
    setInputValue("");
  };

  const handleKeyDown = (e) => {
    if (["Enter", "Tab", ","].includes(e.key)) {
      e.preventDefault();
      commitInput();
    }
    if (e.key === "Backspace" && !inputValue && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const removeEmail = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!dragEmail) return;

    const { field, email } = dragEmail;

    // Add to this target if not already present
    if (!value.find((v) => v.email === email)) {
      onAddToTarget(fieldName, email);
    }

    // Remove from source if it's a different field
    if (field !== fieldName) {
      onRemoveFromSource(field, email);
    }

    // Clear drag context
    setDragEmail(null);
  };

  return (
    <div
      className="flex-1 flex flex-wrap gap-1 items-center ml-2"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      {value.map((item, idx) => (
        <span
          key={idx}
          draggable
          onDragStart={() =>
            setDragEmail({ field: fieldName, email: item.email })
          }
          className="flex items-center bg-blue-100 text-blue-800 rounded-full px-2 py-0.5 text-xs cursor-grab"
          title={`Email: ${item.email}`}
        >
          {item.email}
          <X
            className="w-3 h-3 ml-1 cursor-pointer hover:text-blue-900"
            onClick={() => removeEmail(idx)}
          />
        </span>
      ))}

      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitInput} // 👈 NEW: auto-create pill on blur
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="flex-1 min-w-[100px] outline-none bg-transparent text-sm"
      />
    </div>
  );
}


/**
 * 📧 Main Mail Component
 */
export default function Mail() {
  const [isMinimized, setMinimized] = useState(false);
  const [isMaximized, setMaximized] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [dragEmail, setDragEmail] = useState(null); // track current drag

  const { isOpen, close,payload } = useEmailCreateStore();
  
  const [formData, setFormData] = useState({
    from: "support@webtrixs24.com",
    to: [],
    cc: [],
    bcc: [],
    subject: "",
    html: "",
    attachments: [],
  });

  const fileInput = useRef(null);
  const imgInput = useRef(null);
  const editorRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);

  const reset = () => {
    setFormData({
      from: "support@webtrixs24.com",
      to: [],
      cc: [],
      bcc: [],
      subject: "",
      html: "",
      attachments: [],
    });
    close();
  };

  const insertImage = (file) => {
    if (!file || !editorRef.current) return;
    const reader = new FileReader();
    reader.onload = () => {
      editorRef.current.chain().focus().setImage({ src: reader.result }).run();
      if (imgInput.current) imgInput.current.value = "";
    };
    reader.readAsDataURL(file);
  };

  // --- Drag-drop for attachments/images
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);
  useEffect(() => {
  if (isOpen && payload) {
    const p = payload; // ✅ rename variable, avoid shadowing

    let toEmails = [];
    if (Array.isArray(p.to)) {
      toEmails = p.to.filter(Boolean).map((email) => ({ email: email.trim() }));
    } else if (typeof p.to === "string" && p.to.trim() !== "") {
      toEmails = [{ email: p.to.trim() }];
    }

    if (toEmails.length) {
      setFormData((prev) => ({ ...prev, to: toEmails }));
    }

    // ✅ Optional: Pre-fill CC and BCC if available
    let ccEmails = [];
    if (Array.isArray(p.cc)) {
      ccEmails = p.cc.filter(Boolean).map((email) => ({ email: email.trim() }));
    } else if (typeof p.cc === "string" && p.cc.trim() !== "") {
      ccEmails = [{ email: p.cc.trim() }];
    }

    if (ccEmails.length) {
      setShowCc(true);
      setFormData((prev) => ({ ...prev, cc: ccEmails }));
    }

    let bccEmails = [];
    if (Array.isArray(p.bcc)) {
      bccEmails = p.bcc.filter(Boolean).map((email) => ({ email: email.trim() }));
    } else if (typeof p.bcc === "string" && p.bcc.trim() !== "") {
      bccEmails = [{ email: p.bcc.trim() }];
    }

    if (bccEmails.length) {
      setShowBcc(true);
      setFormData((prev) => ({ ...prev, bcc: bccEmails }));
    }
  }
}, [isOpen, payload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    const y = e.clientY;
    const bodyRect = bodyRef.current?.getBoundingClientRect();
    const footerRect = footerRef.current?.getBoundingClientRect();

    files.forEach((file) => {
      const isOverBody = bodyRect && y >= bodyRect.top && y <= bodyRect.bottom;
      const isOverFooter = footerRect && y >= footerRect.top;

      if (isOverBody && file.type.startsWith("image/")) {
        insertImage(file);
      } else if (isOverFooter || !isOverBody) {
        setFormData((prev) => ({
          ...prev,
          attachments: [...prev.attachments, file],
        }));
      }
    });
  }, []);

  // --- Emoji picker hide outside click
  useEffect(() => {
    const hide = (e) => {
      const el = document.querySelector(".EmojiPickerReact");
      if (el && !el.contains(e.target)) setShowEmoji(false);
    };
    if (showEmoji) document.addEventListener("mousedown", hide);
    return () => document.removeEventListener("mousedown", hide);
  }, [showEmoji]);

  // --- Move logic helpers
  const handleRemoveEmail = (field, email) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((v) => v.email !== email),
    }));
  };

  const handleAddEmail = (field, email) => {
    setFormData((prev) => {
      if (prev[field].some((v) => v.email === email)) return prev;
      return { ...prev, [field]: [...prev[field], { email }] };
    });
  };

  // --- Send
  const handleSend = async () => {
    if (!formData.to.length) {
      toast.error("Please add at least one recipient");
      return;
    }

    setIsSending(true);
    try {
      const payload = {
        toField: formData.to.map((e) => e.email),
        ccField: formData.cc.map((e) => e.email),
        bccField: formData.bcc.map((e) => e.email),
        subject: formData.subject,
        mail_description: formData.html,
        fromField: formData.from,
        attachmentArray: JSON.stringify([]),
      };

      const res = await fetchJson(`${API_BASE_URL}/email/send`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (res?.flag === "S") {
        toast.success("Mail sent successfully!");
        reset();
      } else {
        toast.error(res?.msg || "Failed to send mail.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error sending mail.");
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`z-50 bg-white border shadow-lg flex flex-col rounded-md overflow-hidden transition-all fixed ${
        isMaximized
          ? "top-1/2 right-1/2 -translate-y-1/2 translate-x-1/2 w-[82vw] h-[85vh]"
          : isMinimized
          ? "w-[90vw] h-[40px] bottom-4 right-4 sm:w-[340px]"
          : "w-[96vw] h-[85vh] bottom-4 right-4 sm:w-[620px] sm:h-[540px]"
      }`}
      style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px" }}
    >
      {/* Header */}
      <div className="flex justify-between items-center px-4 h-10 bg-gray-100 border-b">
        <span className="font-medium">New Message</span>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setMinimized(!isMinimized);
              if (!isMinimized) setMaximized(false);
            }}
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setMaximized(!isMaximized);
              setMinimized(false);
            }}
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button onClick={() => reset()}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      {!isMinimized && (
        <>
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* From */}
            <div className="flex items-center border-b px-4 py-2">
              <label className="text-sm text-gray-700">From:</label>
              <span className="ml-2 text-sm text-gray-900">
                {formData.from}
              </span>
            </div>

            {/* To */}
            <div className="flex items-center border-b px-4 py-2 relative">
              <label className="text-sm text-gray-700">To:</label>
              <EmailPillInput
                fieldName="to"
                value={formData.to}
                onChange={(to) => setFormData({ ...formData, to })}
                autoFocus
                dragEmail={dragEmail}
                setDragEmail={setDragEmail}
                onRemoveFromSource={handleRemoveEmail}
                onAddToTarget={handleAddEmail}
              />
              <div className="absolute right-4 flex gap-2 text-blue-600 text-xs">
                {!showCc && (
                  <button
                    onClick={() => setShowCc(true)}
                    className="hover:underline"
                  >
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button
                    onClick={() => setShowBcc(true)}
                    className="hover:underline"
                  >
                    Bcc
                  </button>
                )}
              </div>
            </div>

            {/* CC */}
            {showCc && (
              <div className="flex items-center border-b px-4 py-2">
                <label className="text-sm text-gray-700">Cc:</label>
                <EmailPillInput
                  fieldName="cc"
                  value={formData.cc}
                  onChange={(cc) => setFormData({ ...formData, cc })}
                  dragEmail={dragEmail}
                  setDragEmail={setDragEmail}
                  onRemoveFromSource={handleRemoveEmail}
                  onAddToTarget={handleAddEmail}
                />
                <button
                  onClick={() => setShowCc(false)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* BCC */}
            {showBcc && (
              <div className="flex items-center border-b px-4 py-2">
                <label className="text-sm text-gray-700">Bcc:</label>
                <EmailPillInput
                  fieldName="bcc"
                  value={formData.bcc}
                  onChange={(bcc) => setFormData({ ...formData, bcc })}
                  dragEmail={dragEmail}
                  setDragEmail={setDragEmail}
                  onRemoveFromSource={handleRemoveEmail}
                  onAddToTarget={handleAddEmail}
                />
                <button
                  onClick={() => setShowBcc(false)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Subject */}
            <div className="flex items-center border-b px-4 py-2">
              <label className="text-sm text-gray-700">Subject:</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                className="flex-1 ml-2 outline-none text-sm bg-transparent"
                placeholder="Subject"
              />
            </div>

            {/* Body */}
            <div ref={bodyRef} className="flex-1 overflow-hidden">
              <TextEditorMail
                value={formData.html}
                onChange={(html) => setFormData({ ...formData, html })}
                editorRef={editorRef}
              />
            </div>

            {/* Attachments */}
            {formData.attachments.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-gray-50 border-t">
                {formData.attachments.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center bg-gray-100 px-2 py-1 rounded shadow-sm text-sm"
                  >
                    <span className="truncate max-w-[120px]">{f.name}</span>
                    <button
                      className="ml-2"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          attachments: p.attachments.filter(
                            (_, idx) => idx !== i
                          ),
                        }))
                      }
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            ref={footerRef}
            className="flex justify-between items-center px-4 py-2 border-t bg-gray-50 flex-wrap gap-y-2"
          >
            <div className="flex items-center space-x-3 h-full">
              {/* Send */}
              <button
                onClick={handleSend}
                disabled={isSending}
                className={`flex items-center gap-1 px-4 py-1.5 rounded-3xl text-white text-sm font-medium ${
                  isSending
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#0b57d0] hover:bg-blue-700"
                }`}
              >
                {isSending ? (
                  <>
                    <Send className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Send
                  </>
                )}
              </button>

              {/* Attachments */}
              <input
                ref={fileInput}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setFormData((p) => ({
                      ...p,
                      attachments: [...p.attachments, file],
                    }));
                  }
                }}
              />
              <button
                className="flex items-center justify-center h-8 w-8"
                onClick={() => fileInput.current?.click()}
              >
                <Paperclip className="w-4 h-4" />
              </button>

              {/* Inline Image */}
              <input
                ref={imgInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => insertImage(e.target.files?.[0])}
              />
              <button
                className="flex items-center justify-center h-8 w-8"
                onClick={() => imgInput.current?.click()}
              >
                <Image className="w-4 h-4" />
              </button>

              {/* Emoji */}
              <div className="relative">
                <button
                  className="flex items-center justify-center h-8 w-8"
                  onClick={() => setShowEmoji((p) => !p)}
                >
                  <Laugh className="w-4 h-4" />
                </button>
                {showEmoji && (
                  <div className="absolute bottom-10 left-0 z-50">
                    <EmojiPicker
                      onEmojiClick={(e) => {
                        if (editorRef.current) {
                          editorRef.current
                            .chain()
                            .focus()
                            .insertContent(e.emoji)
                            .run();
                        }
                      }}
                      height={350}
                      width={300}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Trash */}
            <button onClick={reset}>
              <Trash2 className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
